"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  Calendar,
  Clock,
  User,
  BookOpen,
  TrendingUp,
  Award,
} from "lucide-react";
import blogDataDe from "@data/pages/blog.json";
import blogDataEn from "@data/pages/blog.en.json";
import landingPageData from "@data/landing-pages/agentic-bi-tool.json";
import { isWordPressImageUrl, sanitizeImageUrl } from "@utils/imageUrl";
import { useModal } from "@hooks/useModal";
import EEATSTrustIndicators from "@components/sections/EEATSTrustIndicators";
import {
  HeroCTAButton,
  DemoRequestButton,
  BlogReadMoreButton,
  ExternalLinkButton,
} from "@components/ui/IconButtonVariants";
import Badge from "@components/ui/Badge";
import ValuePropositionBadge from "@components/ui/ValuePropositionBadge";
import WebinarCTABox from "@components/sections/WebinarCTABox";
import type { Locale } from "@utils/i18n/config";

const HeaderPublic = dynamic(() => import("@components/layout/HeaderPublic"), {
  ssr: true,
});
const FooterPublic = dynamic(() => import("@components/layout/FooterPublic"), {
  ssr: true,
});
const Modal = dynamic(() => import("@components/modals/Modal"), { ssr: false });
const DemoForm = dynamic(
  () => import("@components/features/demo-form/DemoForm"),
  { ssr: false }
);

interface BlogPost {
  id: number;
  slug: string;
  title: string;
  category: string;
  tags: string[];
  excerpt: string;
  featuredImage: { src: string; alt: string };
  date: string;
  author: string;
  link: string;
  readTime?: string;
  featured?: boolean;
}

/** Shape of one entry in the payload produced by `formatPostListResponse`. */
interface RawPostListItem {
  id?: number;
  slug?: string;
  title?: string;
  category?: string;
  tags?: string[];
  excerpt?: string;
  featuredImage?: { src?: string; alt?: string };
  date?: string;
  author?: string;
  link?: string;
  readTime?: string;
  featured?: boolean;
}

interface BlogListingContentProps {
  locale: Locale;
  /**
   * Post list resolved on the server by the route and handed down as a prop.
   * This is what puts every article link into the SSR HTML — previously the
   * list was fetched client-side from /api/blog/posts, which robots.txt
   * disallows, so crawlers only ever saw loading skeletons and the posts were
   * orphaned from the internal link graph.
   */
  posts: Record<string, RawPostListItem> | RawPostListItem[];
}

function toBlogPosts(
  posts: Record<string, RawPostListItem> | RawPostListItem[],
  locale: Locale
): BlogPost[] {
  const list = Array.isArray(posts) ? posts : Object.values(posts ?? {});
  return list.map((p, index) => {
    const slug = p.slug ?? `post-${index}`;
    return {
      id: typeof p.id === "number" ? p.id : index,
      slug,
      title: p.title ?? "Untitled",
      category: p.category ?? "General",
      tags: p.tags ?? [],
      excerpt: p.excerpt ?? "",
      featuredImage: {
        src:
          p.featuredImage?.src ??
          "/optimized/abis-ki-assist-und-agenten-bot-graph-agent-autonomes-data-governance.webp",
        alt: p.featuredImage?.alt ?? p.title ?? "Blog article",
      },
      date: p.date ?? "",
      author: p.author ?? "DBI Analytics",
      link: p.link ?? (locale === "en" ? `/en/blog/${slug}` : `/blog/${slug}`),
      readTime: p.readTime ?? "5 min",
      featured: p.featured ?? false,
    };
  });
}

export default function BlogListingContent({
  locale,
  posts,
}: BlogListingContentProps) {
  const blogData = locale === "en" ? blogDataEn : blogDataDe;
  const dateLocale = locale === "en" ? "en-US" : "de-DE";

  const blogPosts = useMemo(() => toBlogPosts(posts, locale), [posts, locale]);
  const demoModal = useModal();

  const sortedBlogPosts = useMemo(
    () =>
      [...blogPosts].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      ),
    [blogPosts]
  );

  const featuredPost = useMemo(
    () => sortedBlogPosts.find((p) => p.featured) || sortedBlogPosts[0],
    [sortedBlogPosts]
  );

  const otherPosts = useMemo(
    () => sortedBlogPosts.filter((p) => p.id !== featuredPost?.id),
    [sortedBlogPosts, featuredPost]
  );

  const heroSection = blogData.heroSection;
  const featuredSection = blogData.featuredSection;
  const ctaSection = blogData.ctaSection;
  const blogListingsSection = {
    ...blogData.blogListingsSection,
    subtitle: `${sortedBlogPosts.length} ${blogData.blogListingsSection.subtitle}`,
  };

  const featuredImageSrc = featuredPost
    ? sanitizeImageUrl(featuredPost.featuredImage.src)
    : "";

  const siteBase = "https://dbi-analytics.de";
  const blogIndexUrl = locale === "en" ? `${siteBase}/en/blog` : `${siteBase}/blog`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: locale === "en" ? "DBI Analytics Blog" : "DBI Analytics Blog",
    description:
      locale === "en"
        ? "Expert insights on Agentic BI, Microsoft Fabric, AI governance and enterprise data strategy"
        : "Power BI Blog für Datenanalyse & Business Intelligence - Expertenwissen von zertifizierten Power BI Spezialisten",
    url: blogIndexUrl,
    inLanguage: locale === "en" ? "en-US" : "de-DE",
    publisher: {
      "@type": "Organization",
      name: "DBI Analytics",
      url: siteBase,
      logo: { "@type": "ImageObject", url: `${siteBase}/logo-word.svg` },
    },
    blogPost: sortedBlogPosts.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      url: `${siteBase}${post.link}`,
      datePublished: post.date,
      author: { "@type": "Person", name: post.author },
      image: {
        "@type": "ImageObject",
        url: sanitizeImageUrl(post.featuredImage.src),
        alt: post.featuredImage.alt,
      },
      keywords: post.tags.join(", "),
      articleSection: post.category,
      inLanguage: locale === "en" ? "en-US" : "de-DE",
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <HeaderPublic />
      <main
        className="min-h-screen bg-white"
        role="main"
        aria-label={locale === "en" ? "Blog articles and insights" : "Blog-Artikel und Fachwissen"}
      >
        {/* Hero Section */}
        <section
          className="relative bg-gradient-to-br from-abis-primary to-abis-primary/90 py-20 md:py-44 overflow-hidden"
          role="banner"
          aria-labelledby="hero-title"
        >
          <div
            className="absolute inset-0 bg-gradient-to-r from-abis-accent/10 via-transparent to-abis-accent/10"
            aria-hidden="true"
          />
          <div className="container mx-auto pt-28 pb-10 px-4 max-w-6xl relative z-10">
            <div className="text-center">
              <h1
                id="hero-title"
                className="text-[2rem] md:text-[3rem] font-normal text-white mb-6"
                style={{ fontFamily: "var(--font-kanit, Kanit, sans-serif)" }}
                itemProp="headline"
              >
                {heroSection.title}
                {heroSection.accentText && (
                  <span className="text-abis-accent"> {heroSection.accentText}</span>
                )}
              </h1>
              <p
                className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8"
                itemProp="description"
              >
                {heroSection.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <HeroCTAButton
                  href={heroSection.primaryButton.href}
                  ariaLabel={heroSection.primaryButton.text}
                  className="shadow-lg hover:shadow-xl"
                >
                  {heroSection.primaryButton.text}
                </HeroCTAButton>
                <Link
                  href={heroSection.secondaryButton.href}
                  className="btn btn-hero bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold py-4 px-8 rounded-xl text-lg border border-white/20 hover:border-white/40 whitespace-nowrap transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-abis-primary"
                >
                  {heroSection.secondaryButton.text}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Post Section */}
        {featuredPost && (
          <section
            className="py-20 bg-gray-50"
            aria-labelledby="featured-section-title"
            itemScope
            itemType="https://schema.org/BlogPosting"
          >
            <div className="container mx-auto px-4 max-w-6xl">
              <div className="text-center mb-16">
                <ValuePropositionBadge
                  seoChip={featuredSection.seoChip}
                  titleLine1={featuredSection.titleLine1}
                  titleLine2={featuredSection.titleLine2}
                  icon={Award}
                />
              </div>
              <article
                className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer group border border-gray-100"
                aria-label={`${blogData.labels.featuredAriaPrefix}${featuredPost.title}`}
              >
                <Link href={featuredPost.link} className="block">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                    <div className="relative h-64 lg:h-auto overflow-hidden">
                      <Image
                        src={featuredImageSrc}
                        alt={featuredPost.featuredImage.alt}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                        sizes="(max-width: 768px) 100vw, 50vw"
                        itemProp="image"
                        priority
                        unoptimized={isWordPressImageUrl(featuredImageSrc)}
                      />
                      <div className="absolute top-4 left-4">
                        <span
                          className="bg-abis-accent px-3 py-1 rounded-full text-sm font-semibold text-primary"
                          itemProp="articleSection"
                        >
                          {featuredPost.category}
                        </span>
                      </div>
                      {featuredPost.readTime && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-black/50 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                            {featuredPost.readTime}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <h3
                        className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-abis-primary transition-colors"
                        style={{ fontFamily: "var(--font-kanit, Kanit, sans-serif)" }}
                        itemProp="headline"
                      >
                        {featuredPost.title}
                      </h3>
                      <p
                        className="text-gray-600 mb-6 text-lg leading-relaxed line-clamp-4"
                        itemProp="description"
                      >
                        {featuredPost.excerpt.length > 120
                          ? `${featuredPost.excerpt.substring(0, 120)}...`
                          : featuredPost.excerpt}
                      </p>
                      <div
                        className="space-y-3 mb-6"
                        itemProp="author"
                        itemScope
                        itemType="https://schema.org/Person"
                      >
                        <div className="flex items-center text-sm text-gray-500">
                          <User className="w-4 h-4 mr-2" aria-hidden="true" />
                          <span itemProp="name">{featuredPost.author}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="w-4 h-4 mr-2" aria-hidden="true" />
                          <time itemProp="datePublished" dateTime={featuredPost.date}>
                            {new Date(featuredPost.date).toLocaleDateString(dateLocale, {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </time>
                        </div>
                        {featuredPost.readTime && (
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="w-4 h-4 mr-2" aria-hidden="true" />
                            <span>{featuredPost.readTime}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-6" itemProp="keywords">
                        {featuredPost.tags.slice(0, 4).map((tag, i) => (
                          <span
                            key={i}
                            className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                          >
                            #{tag}
                          </span>
                        ))}
                        {featuredPost.tags.length > 4 && (
                          <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                            +{featuredPost.tags.length - 4} {blogData.labels.tagMore}
                          </span>
                        )}
                      </div>
                      <BlogReadMoreButton
                        className="w-full"
                        ariaLabel={`${blogData.labels.readMore}: ${featuredPost.title}`}
                      >
                        {blogData.labels.readMore}
                      </BlogReadMoreButton>
                    </div>
                  </div>
                </Link>
              </article>
            </div>
          </section>
        )}

        {/* Blog Listings Section */}
        <section
          id="blog-listings"
          className="py-20 bg-white"
          aria-labelledby="blog-listings-title"
        >
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-16">
              <h2 id="blog-listings-title" className="section-header" itemProp="name">
                <span className="text-abis-primary">
                  {blogData.blogListingsSection.allArticlesHeading1}
                </span>{" "}
                <span className="text-abis-accent">
                  {blogData.blogListingsSection.allArticlesHeading2}
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" itemProp="description">
                {blogListingsSection.subtitle}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherPosts && otherPosts.length > 0 ? (
                otherPosts.map((post, index) => {
                  const postImageSrc = sanitizeImageUrl(post.featuredImage.src);
                  return (
                    <article
                      key={`blog-post-${post.id}-${post.slug}-${index}`}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group border border-gray-100"
                      aria-label={`${blogData.labels.articleAriaPrefix}${post.title}`}
                    >
                      <Link href={post.link} className="block">
                        <div className="relative h-48 overflow-hidden cursor-pointer">
                          <Image
                            src={postImageSrc}
                            alt={post.featuredImage.alt}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-300"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            itemProp="image"
                            unoptimized={isWordPressImageUrl(postImageSrc)}
                          />
                          <div className="absolute top-4 left-4">
                            <span
                              className="bg-abis-accent px-3 py-1 rounded-full text-sm font-semibold text-primary"
                              itemProp="articleSection"
                            >
                              {post.category}
                            </span>
                          </div>
                          {post.readTime && (
                            <div className="absolute top-4 right-4">
                              <span className="bg-black/50 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                                {post.readTime}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <h3
                            className="text-xl font-bold text-gray-900 mb-3 group-hover:text-abis-primary transition-colors"
                            style={{ fontFamily: "var(--font-kanit, Kanit, sans-serif)" }}
                            itemProp="headline"
                          >
                            {post.title}
                          </h3>
                          <p className="text-gray-600 mb-4 line-clamp-3" itemProp="description">
                            {post.excerpt}
                          </p>
                          <div
                            className="space-y-2 mb-4"
                            itemProp="author"
                            itemScope
                            itemType="https://schema.org/Person"
                          >
                            <div className="flex items-center text-sm text-gray-500">
                              <User className="w-4 h-4 mr-2" aria-hidden="true" />
                              <span itemProp="name">{post.author}</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <Calendar className="w-4 h-4 mr-2" aria-hidden="true" />
                              <time itemProp="datePublished" dateTime={post.date}>
                                {new Date(post.date).toLocaleDateString(dateLocale, {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </time>
                            </div>
                            {post.readTime && (
                              <div className="flex items-center text-sm text-gray-500">
                                <Clock className="w-4 h-4 mr-2" aria-hidden="true" />
                                <span>{post.readTime}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 mb-4" itemProp="keywords">
                            {post.tags.slice(0, 3).map((tag, i) => (
                              <span
                                key={i}
                                className="bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-xs"
                              >
                                #{tag}
                              </span>
                            ))}
                            {post.tags.length > 3 && (
                              <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-xs">
                                +{post.tags.length - 3} {blogData.labels.tagMore}
                              </span>
                            )}
                          </div>
                          <BlogReadMoreButton
                            className="w-full"
                            ariaLabel={`${blogData.labels.readMore}: ${post.title}`}
                          >
                            {blogData.labels.readMore}
                          </BlogReadMoreButton>
                        </div>
                      </Link>
                    </article>
                  );
                })
              ) : (
                <div className="col-span-full text-center py-12" role="status" aria-live="polite">
                  <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" aria-hidden="true" />
                  <p className="text-lg text-gray-600">{blogData.blogListingsSection.emptyState.title}</p>
                  <p className="text-gray-500 mt-2">{blogData.blogListingsSection.emptyState.subtitle}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* E-E-A-T-S Trust Indicators */}
        <section className="py-16 bg-gray-50" aria-labelledby="expertise-section-title">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-12">
              <Badge variant="solid" className="mb-6">
                {blogData.trustSection.badge}
              </Badge>
              <h2 className="section-header" id="expertise-section-title" itemProp="name">
                <span className="text-abis-primary">{blogData.trustSection.titlePart1}</span>{" "}
                <span className="text-abis-accent">{blogData.trustSection.titleAccent}</span>{" "}
                <span className="text-abis-primary">{blogData.trustSection.titlePart2}</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" itemProp="description">
                {blogData.trustSection.description}
              </p>
            </div>
            <EEATSTrustIndicators variant="compact" />
          </div>
        </section>

        {/* Webinar CTA Box */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <WebinarCTABox />
          </div>
        </section>

        {/* CTA Section */}
        <section
          className="py-20 bg-gradient-to-br from-abis-primary to-abis-primary/80 relative overflow-hidden"
          aria-labelledby="cta-section-title"
        >
          <div
            className="absolute inset-0 bg-gradient-to-r from-abis-accent/10 via-transparent to-abis-accent/10"
            aria-hidden="true"
          />
          <div className="container mx-auto px-4 max-w-6xl relative z-10">
            <div className="text-center">
              <Badge variant="solid" className="mb-6">{ctaSection.seoChip}</Badge>
              <h2
                id="cta-section-title"
                className="section-header font-kanit"
                itemProp="name"
              >
                <span className="text-white">{ctaSection.title}</span>
              </h2>
              <p
                className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-12"
                itemProp="description"
              >
                {ctaSection.description}
              </p>
              <div
                className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
                role="list"
                aria-label={ctaSection.seoChip}
              >
                {ctaSection.benefits.map((benefit, i) => {
                  const IconComponent =
                    benefit.icon === "TrendingUp"
                      ? TrendingUp
                      : benefit.icon === "BookOpen"
                      ? BookOpen
                      : Award;
                  return (
                    <div
                      key={i}
                      className="bg-gradient-to-br from-abis-primary/30 via-abis-primary/45 to-abis-primary/35 rounded-2xl p-6 border border-primary shadow-lg"
                      role="listitem"
                    >
                      <IconComponent className="w-8 h-8 text-abis-accent mx-auto mb-4" aria-hidden="true" />
                      <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-white/80 text-sm">{benefit.description}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <DemoRequestButton
                  onClick={() => demoModal.open()}
                  ariaLabel={ctaSection.primaryButton.text}
                >
                  {ctaSection.primaryButton.text}
                </DemoRequestButton>
                <ExternalLinkButton
                  href="/meet"
                  ariaLabel={ctaSection.secondaryButton.text}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/20 hover:border-white/40"
                >
                  {ctaSection.secondaryButton.text}
                </ExternalLinkButton>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Modal
        isOpen={demoModal.isOpen}
        onClose={() => demoModal.close()}
        title={landingPageData.form.title}
        variant="form"
        size="lg"
        className="max-w-4xl"
        fullscreenOnMobile={true}
        showCloseButton={true}
        closeOnOverlayClick={true}
        closeOnEscape={true}
      >
        <DemoForm onClose={() => demoModal.close()} />
      </Modal>

      <FooterPublic locale={locale} />
    </>
  );
}
